/**
 * Auto Date Shortcode Client-Side Renderer
 *
 * Renders dates for [auto_date] and [auto_date_exact] shortcodes on the client side
 * to avoid CDN caching issues. The Python shortcode handlers output empty <span>
 * elements with data attributes that this script uses to render the current date.
 */

import { domReady } from '../../../../project/static/project/js/utils/dom_ready.js';

(function() {
  'use strict';

  // Date format mapping (must match DATE_FORMATS in pages/constants.py)
  const DATE_FORMATS = {
    'abbreviated_month_year': { month: 'short', year: 'numeric' },
    'month_year': { month: 'long', year: 'numeric' },
    'month_date_year': { month: 'long', day: 'numeric', year: 'numeric' },
    'year': { year: 'numeric' }
  };

  /**
   * Format a date according to the specified format key
   * @param {Date} date - The date to format
   * @param {string} formatKey - The format key from DATE_FORMATS
   * @returns {string} The formatted date string
   */
  function formatDate(date, formatKey) {
    const format = DATE_FORMATS[formatKey];

    if (!format) {
      console.error(`[auto_date_shortcode] Unknown date format: ${formatKey}`);
      return '';
    }

    // Use Intl.DateTimeFormat for proper locale-aware formatting
    const formatter = new Intl.DateTimeFormat('en-US', format);
    let formatted = formatter.format(date);

    // For month_date_year format, we need to handle the comma placement
    // Intl.DateTimeFormat gives us "January 5, 2025" which is what we want
    // For other formats, the output is correct as-is

    return formatted;
  }

  /**
   * Add days to a date
   * @param {Date} date - The base date
   * @param {number} days - Number of days to add
   * @returns {Date} The new date
   */
  function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  /**
   * Render any auto-date shortcode elements that have not been rendered yet.
   * Idempotent: already-rendered elements are flagged with data-rendered and skipped,
   * so this is safe to call repeatedly (e.g. on every DOM mutation).
   */
  function renderAutoDateShortcodes() {
    const elements = document.querySelectorAll('.js-shortcode-auto-date:not([data-rendered])');

    elements.forEach(function(element) {
      const formatKey = element.getAttribute('data-date-format');
      const timeDelta = parseInt(element.getAttribute('data-time-delta'), 10) || 0;

      if (!formatKey) {
        console.error('[auto_date_shortcode] Missing data-date-format attribute', element);
        return;
      }

      // Calculate the date with time delta
      const baseDate = new Date();
      const targetDate = addDays(baseDate, timeDelta);

      // Format and insert the date
      const formattedDate = formatDate(targetDate, formatKey);
      element.textContent = formattedDate;
      element.setAttribute('data-rendered', 'true');
    });
  }

  /**
   * Watch for spans injected or re-rendered after the initial pass (A/B tests,
   * personalization swaps, lazy-loaded blocks) and render them as they appear.
   */
  function observeForNewShortcodes() {
    const observer = new MutationObserver(function() {
      renderAutoDateShortcodes();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    renderAutoDateShortcodes();
    observeForNewShortcodes();
  }

  domReady(init);
})();
